import { useState, useEffect } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from '@/components/ui/dialog';
import type { TodoItemResponse, TodoProjectResponse } from '@/lib/api';
import { useTodoChecklist } from '../hooks';
import { SectionSelect, type SelectableSection } from './SectionSelect';
import { formatCreatedDate } from '../lib/dates';
import { ChecklistEditor } from './ChecklistEditor';
import { ProjectPicker } from './ProjectPicker';

interface EditItemDialogProps {
  open: boolean;
  onClose: () => void;
  item: TodoItemResponse | null;
  sections: SelectableSection[];
  /**
   * The section the picker should start on, overriding the item's own
   * `sectionId`. The My Tasks board passes the task's current PERSONAL column
   * here (the picker lists personal sections there, and `onSave`'s `sectionId`
   * is the chosen personal section — the board turns it into a placement move,
   * not a shared re-file). Omit on the List view to use `item.sectionId`.
   */
  initialSectionId?: string | null;
  /** Label for the null section option (default "Unsorted"). */
  sectionNoneLabel?: string;
  /** Whether to offer the null/"Unsorted" section option. The List view hides it
   * from a member who may not move a task to Unsorted. Defaults to true. */
  showNoneSection?: boolean;
  /** Holder id — needed to manage the checklist. */
  holderId: string | null;
  /** Workspace projects for the project picker. */
  projects?: TodoProjectResponse[];
  /** Inline project creation (owner/admin only); omit to hide "+ New project". */
  onCreateProject?: (name: string) => Promise<TodoProjectResponse>;
  onSave: (
    itemId: string,
    input: {
      title?: string;
      // `null` clears the description; `undefined` means "unchanged".
      description?: string | null;
      sectionId?: string | null;
      projectId?: string | null;
    },
  ) => void;
  /**
   * Whether the editor may re-file the item (change its Section) and toggle /
   * delete existing checklist items. Managers and the assignee get `true`; a
   * reviewer annotating an in-review task gets `false` — they can still edit the
   * title / description and ADD a checklist item, but not move it or alter the
   * owner's checklist.
   */
  canEditStructure?: boolean;
}

export function EditItemDialog({
  open,
  onClose,
  item,
  sections,
  initialSectionId,
  sectionNoneLabel,
  showNoneSection = true,
  holderId,
  projects = [],
  onCreateProject,
  onSave,
  canEditStructure = true,
}: EditItemDialogProps) {
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [sectionId, setSectionId] = useState<string>('none');
  const [projectId, setProjectId] = useState<string | null>(null);

  // The checklist is only shown for top-level tasks.
  const isTopLevel = !!item && item.parentItemId === null;
  const {
    items: checklistItems,
    createItem,
    setDone,
    deleteItem,
    reorder,
  } = useTodoChecklist(
    holderId,
    item?.listId ?? '',
    isTopLevel ? (item?.itemId ?? null) : null,
    open,
  );
  const checklistTotal = checklistItems.length;
  const checklistDone = checklistItems.filter((c) => c.isDone).length;

  // The section the picker starts on: the caller-supplied override (the board's
  // current personal column) when given, else the item's own shared section.
  const baseSectionId =
    initialSectionId !== undefined ? initialSectionId : (item?.sectionId ?? null);

  useEffect(() => {
    if (open && item) {
      setTitle(item.title);
      setDescription(item.description ?? '');
      setSectionId(baseSectionId ?? 'none');
      setProjectId(item.projectId ?? null);
    }
  }, [open, item, baseSectionId]);

  function handleOpenChange(nextOpen: boolean) {
    if (!nextOpen) {
      onClose();
    }
  }

  function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (!item || !title.trim()) return;

    const newSection = sectionId === 'none' ? null : sectionId;
    const oldSection = baseSectionId;
    const oldProject = item.projectId ?? null;

    onSave(item.itemId, {
      title: title.trim(),
      // `null` is the clear sentinel (the op treats `undefined` as "no change",
      // so `|| undefined` would make emptying the field a silent no-op).
      description: description.trim() ? description.trim() : null,
      ...(newSection !== oldSection ? { sectionId: newSection } : {}),
      ...(projectId !== oldProject ? { projectId } : {}),
    });
    onClose();
  }

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent className="flex max-h-[90dvh] max-w-md flex-col">
        <DialogHeader>
          <DialogTitle>Edit Item</DialogTitle>
          <DialogDescription>Update the item details.</DialogDescription>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="flex min-h-0 flex-1 flex-col gap-4">
          {/* Scrolls when the content (esp. a long checklist) exceeds the viewport
              — keeps the header + footer pinned. Sized in dvh for mobile. */}
          <div className="min-h-0 flex-1 space-y-4 overflow-y-auto pr-1">
            <div className="space-y-2">
              <Label htmlFor="edit-item-title">Title</Label>
              <Input
                id="edit-item-title"
                value={title}
                onChange={(e) => setTitle(e.target.value)}
                required
                autoFocus
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="edit-item-description">Description (optional)</Label>
              <Textarea
                id="edit-item-description"
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                rows={2}
              />
            </div>

            <div className="space-y-2">
              <Label>Section</Label>
              <SectionSelect
                sections={sections}
                value={sectionId}
                onChange={setSectionId}
                disabled={!canEditStructure}
                noneLabel={sectionNoneLabel}
                showNone={showNoneSection}
              />
            </div>

            {/* Project — a cross-cutting grouping, orthogonal to the section. Only
                on top-level tasks (sub-tasks inherit their parent's project). */}
            {isTopLevel && (
              <div className="space-y-2">
                <Label>Project</Label>
                <ProjectPicker
                  value={projectId}
                  projects={projects}
                  onChange={setProjectId}
                  onCreateProject={onCreateProject}
                  disabled={!canEditStructure}
                />
              </div>
            )}

            {isTopLevel && (
              <ChecklistEditor
                items={checklistItems.map((c) => ({
                  id: c.checklistItemId,
                  title: c.title,
                  done: c.isDone,
                }))}
                doneCount={checklistDone}
                totalCount={checklistTotal}
                onToggle={(id, done) => setDone(id, !done)}
                onDelete={(id) => deleteItem(id)}
                onAdd={(t) => createItem(t)}
                onReorder={(order) => reorder(order)}
                canModifyExisting={canEditStructure}
              />
            )}

            {item && (
              <p className="pt-1 text-xs text-muted-foreground">
                Created {formatCreatedDate(item.createdAt)}
              </p>
            )}

            {item?.reportedBy && (
              <p className="text-xs text-muted-foreground">Reported by: {item.reportedBy}</p>
            )}
          </div>

          <DialogFooter>
            <Button type="button" variant="outline" onClick={onClose}>
              Cancel
            </Button>
            <Button type="submit" disabled={!title.trim()}>
              Save
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
